import React, { createContext, useContext, useState, useEffect } from 'react';
import { CONTACT } from '@/config/contact';

type Language = 'el' | 'en';
type TranslationValue = string | string[];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  tList: (key: string) => string[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations: Record<Language, Record<string, TranslationValue>> = {
  el: {
    // Navigation
    'nav.home': 'Αρχική',
    'nav.services': 'Υπηρεσίες',
    'nav.about': 'Σχετικά',
    'nav.projects': 'Έργα',
    'nav.pricing': 'Τιμές',
    'nav.contact': 'Επικοινωνία',
    'nav.consultation': 'Δωρεάν Συμβουλευτική',
    
    // Hero Section
    'hero.title.part1': 'Βοηθάμε Τοπικές Επιχειρήσεις να ',
    'hero.title.part2': 'Αναπτυχθούν Online',
    'hero.description': 'Δημιουργούμε ιστοσελίδες και στρατηγικό marketing που φέρνει νέους πελάτες, ενισχύει την εικόνα σας και μετατρέπει την online παρουσία σας σε επιχειρηματικό πλεονέκτημα.',
    'hero.cta.consultation': 'Δωρεάν Συμβουλευτική',
    'hero.cta.game': 'Σπατάλησε το Χρονο Σου',
    
    // Services Section
    'services.title': 'Πώς Βοηθάμε τις Τοπικές Επιχειρήσεις',
    'services.description': 'Προσφέρουμε εξειδικευμένες υπηρεσίες που έχουν σχεδιαστεί για να βοηθήσουν την επιχείρησή σας να αναπτυχθεί στο διαδίκτυο.',
    'services.website.title': 'Κατασκευή Ιστοσελίδων',
    'services.website.description': 'Κατασκευάζουμε επαγγελματικές ιστοσελίδες που φορτώνουν γρήγορα, λειτουργούν άψογα σε κινητά και έχουν σχεδιαστεί για να μετατρέπουν επισκέπτες σε πελάτες.',
    'services.eshop.title': 'Κατασκευή E‑Shop',
    'services.eshop.description': 'Στήνουμε σύγχρονα ηλεκτρονικά καταστήματα, ασφαλή και εύκολα στη διαχείριση, με λειτουργίες πληρωμών, αποστολών και αποθέματος. Σας βοηθάμε να πουλάτε online σε ευρύτερο κοινό.',
    'services.branding.title': 'Branding',
    'services.branding.description': 'Δημιουργούμε ολοκληρωμένες εταιρικές ταυτότητες με έμφαση στη συνοχή, την αναγνωρισιμότητα και τη στρατηγική διαφοροποίηση της επιχείρησής σας.',
    'services.marketing.title': 'Στρατηγικές Μάρκετινγκ',
    'services.marketing.description': 'Σχεδιάζουμε και υλοποιούμε marketing στρατηγικές που φέρνουν μετρήσιμα αποτελέσματα — από SEO και Google Ads μέχρι οργανικό περιεχόμενο και διαφημίσεις στα social media.',
    'services.mobile.title': 'Κατασκευή Mobile App',
    'services.mobile.description': 'Αναπτύσσουμε mobile εφαρμογές για επιχειρήσεις που θέλουν να προσφέρουν υπηρεσίες, κρατήσεις ή προϊόντα μέσω κινητού, με εύχρηστο σχεδιασμό και γρήγορη απόκριση.',
    'services.content.title': 'Παραγωγή Περιεχομένου',
    'services.content.description': 'Παράγουμε επαγγελματικό περιεχόμενο, όπως βίντεο, γραφικά και φωτογραφίες, που αναδεικνύουν την επιχείρησή σας και ενισχύουν τη διαδικτυακή σας εικόνα.',

    // Content Production Page - Hero Section
    'content.hero.title': 'Παραγωγή Περιεχομένου που Ενισχύει την Εικόνα και τις Πωλήσεις',
    'content.hero.subtitle': 'Παράγουμε περιεχόμενο που ξεχωρίζει — βίντεο, φωτογραφίες, κείμενα και γραφικά που συνδέονται με τη στρατηγική σας και αυξάνουν το engagement.',
    'content.hero.primaryCTA': 'Δωρεάν Συμβουλευτική',
    'content.hero.secondaryCTA': 'Δείτε Case Studies',
    'content.hero.trust1': 'Content-First Προσέγγιση',
    'content.hero.trust2': 'Επαγγελματική Παραγωγή & Editing',
    'content.hero.trust3': 'Ενιαία Εμφάνιση σε Όλα τα Κανάλια',

    // Content Production Page - Problem/Outcome Section
    'content.problemOutcome.title': 'Από το Περιεχόμενο στην Ανάπτυξη',
    'content.problemOutcome.problems': [
      'Μη επαγγελματικό ή ασύνδετο περιεχόμενο',
      'Έλλειψη συνέπειας σε social και website',
      'Χαμηλό engagement ή αναγνωρισιμότητα'
    ],
    'content.problemOutcome.outcomes': [
      'Ισχυρή οπτική ταυτότητα σε όλα τα μέσα',
      'Ποιοτικό περιεχόμενο που μεταδίδει εμπιστοσύνη',
      'Στρατηγική παρουσία που αυξάνει leads & πωλήσεις'
    ],

    // Content Production Page - Features Section
    'content.features.title': 'Κύρια Χαρακτηριστικά',
    'content.features.video.title': 'Video Production',
    'content.features.video.description': 'Σενάρια, γυρίσματα και editing για social, ads ή website.',
    'content.features.photography.title': 'Product & Brand Photography',
    'content.features.photography.description': 'Επαγγελματική φωτογράφιση προϊόντων, χώρων ή ομάδας.',
    'content.features.social.title': 'Social Media Visuals',
    'content.features.social.description': 'Custom γραφικά, carousels και templates για συνεκτική παρουσία.',
    'content.features.copywriting.title': 'Copywriting & Storytelling',
    'content.features.copywriting.description': 'Κείμενα που εκφράζουν την ταυτότητα και τα οφέλη της επιχείρησής σας.',
    'content.features.calendar.title': 'Content Calendar & Διανομή',
    'content.features.calendar.description': 'Πλάνο περιεχομένου για όλα τα κανάλια και αυτοματοποίηση δημοσιεύσεων.',
    'content.features.tracking.title': 'Performance Tracking',
    'content.features.tracking.description': 'Αναφορές engagement, reach και CTR για συνεχή βελτιστοποίηση.',

    // Content Production Page - Process Section
    'content.process.title': 'Διαδικασία Υλοποίησης',
    'content.process.step1.title': 'Brief & Στρατηγική',
    'content.process.step1.description': 'Καθορισμός στόχων, ύφους και κοινού.',
    'content.process.step2.title': 'Δημιουργία & Παραγωγή',
    'content.process.step2.description': 'Γυρίσματα, φωτογράφιση, σχεδιασμός και copywriting.',
    'content.process.step3.title': 'Editing & Βελτιστοποίηση',
    'content.process.step3.description': 'Προσαρμογή για κάθε πλατφόρμα (YouTube, Instagram, website).',
    'content.process.step4.title': 'Παράδοση & Ανάλυση',
    'content.process.step4.description': 'Τελικά αρχεία, templates και αναφορές απόδοσης.',

    // Content Production Page - Deliverables Section
    'content.deliverables.title': 'Παραδοτέα',
    'content.deliverables.items': [
      'Video & photo assets (MP4, JPG, PNG)',
      'Γραφικά & templates (PSD/Canva/Figma)',
      'Copywriting & captions',
      'Content calendar (Excel ή Notion)',
      'Monthly performance report'
    ],

    // Content Production Page - Pricing Section
    'content.pricing.title': 'Πακέτα & Τιμές',
    'content.pricing.starter.name': 'Starter',
    'content.pricing.starter.description': '5 κομμάτια περιεχομένου / μήνα (π.χ. reels ή carousels)',
    'content.pricing.growth.name': 'Growth',
    'content.pricing.growth.description': '10–15 κομμάτια με πλήρες calendar & performance tracking',
    'content.pricing.dominance.name': 'Dominance',
    'content.pricing.dominance.description': '20+ κομμάτια / μήνα με στρατηγικό storytelling και παραγωγή video',
    'content.pricing.cta': 'Δωρεάν Συμβουλευτική',

    // Content Production Page - Case Studies Section
    'content.caseStudies.title': 'Case Studies',
    'content.caseStudies.voltbuild.title': 'VoltBuild',
    'content.caseStudies.voltbuild.description': 'Παραγωγή βίντεο και καρουζέλ για social media με 3x engagement',
    'content.caseStudies.kts.title': 'KTS Woodart',
    'content.caseStudies.kts.description': 'Φωτογράφιση & περιεχόμενο για website και Instagram',
    'content.caseStudies.pixelpro.title': 'PixelPro Greece',
    'content.caseStudies.pixelpro.description': 'Εκπαιδευτικά reels & infographics για digital marketing',

    // Content Production Page - FAQ Section
    'content.faq.title': 'Συχνές Ερωτήσεις',
    'content.faq.q1': 'Παράγετε και βίντεο;',
    'content.faq.a1': 'Ναι, αναλαμβάνουμε πλήρη παραγωγή (λήψη, μοντάζ, υποτίτλους, voice-over).',
    'content.faq.q2': 'Πόσο συχνά δημιουργείται νέο περιεχόμενο;',
    'content.faq.a2': 'Ανάλογα με το πλάνο — συνήθως εβδομαδιαία ή μηνιαία βάση.',
    'content.faq.q3': 'Μπορείτε να διαχειριστείτε και τα social;',
    'content.faq.a3': 'Ναι, μέσω των πακέτων διαχείρισης social media.',
    'content.faq.q4': 'Παρέχετε αναφορές απόδοσης;',
    'content.faq.a4': 'Φυσικά — αποστέλλουμε μηνιαίο report με insights και προτάσεις.',

    // Content Production Page - Final CTA Section
    'content.finalCTA.title': 'Θέλετε περιεχόμενο που κάνει τη διαφορά;',
    'content.finalCTA.subtitle': 'Δημιουργούμε visual και κείμενα που εμπνέουν και φέρνουν αποτελέσματα.',
    'content.finalCTA.cta': 'Δωρεάν Συμβουλευτική',

    // Content Production Page - Sidebar Contact
    'content.sidebar.phone': CONTACT.phoneDisplay,
    'content.sidebar.email': CONTACT.email,
    'content.sidebar.cta': 'Κλείστε Δωρεάν Κλήση 20′',
    'content.sidebar.related1': 'Branding',
    'content.sidebar.related2': 'Στρατηγικές Μάρκετινγκ',
    'content.sidebar.related3': 'Κατασκευή Ιστοσελίδων',

    // Content Production Page - SEO Metadata
    'content.seo.title': 'Παραγωγή Περιεχομένου – Βίντεο, Φωτογραφία & Copywriting | PixelPro Solutions',
    'content.seo.description': 'Δημιουργούμε επαγγελματικό περιεχόμενο που ενισχύει την εικόνα και φέρνει αποτελέσματα. Video, φωτογραφία, κείμενα & social visuals με στρατηγική προσέγγιση.',
    
    // Projects Section
    'projects.title': 'Έργα & Case Studies',
    'projects.description': 'Δείτε πώς βοηθήσαμε επιχειρήσεις στην Ελλάδα και διεθνώς να αναπτυχθούν ψηφιακά',
    
    // Pricing Section
    'pricing.title': 'Πακέτα Υπηρεσιών PixelPro',
    'pricing.description': 'Επιλέξτε το πακέτο που ταιριάζει στην επιχειρησή σας και ενισχύστε την ψηφιακή σας παρουσία.',
    'pricing.starter.name': 'Starter',
    'pricing.growth.name': 'Growth', 
    'pricing.dominance.name': 'Dominance',
    'pricing.month': '/μήνα',
    'pricing.select': 'Επιλέξτε το',
    'pricing.package': 'Πακέτο',
    'pricing.unsure': 'Δεν είστε σίγουροι; Δωρεάν Συμβουλευτική',
    'pricing.total': 'Σύνολο:',
    'pricing.cancel': 'Ακύρωση ανά πάσα στιγμή.',
    'pricing.faq.title': 'Συχνές Ερωτήσεις',
    
    // About Section
    'about.title': 'Σχετικά με Εμάς',
    'about.description.part1': 'Η PixelPro Solutions είναι μια δημιουργική ομάδα με έδρα την Ελλάδα και το Ηνωμένο Βασίλειο, αφιερωμένη στο να βοηθά τοπικές επιχειρήσεις να αναπτυχθούν ψηφιακά.',
    'about.description.part2': 'Με πάνω από 15 χρόνια εμπειρίας σε branding, ιστοσελίδες και στρατηγικές marketing, προσφέρουμε πρακτικές λύσεις που φέρνουν πραγματικά αποτελέσματα, όχι απλώς εντυπωσιακές εικόνες.',
    'about.description.part3': 'Δουλεύουμε στενά με κάθε πελάτη για να κατανοήσουμε εις βάθος την επιχείρηση, τον κλάδο και τις ανάγκες του. Από την πρώτη συμβουλευτική έως την τελική υλοποίηση, ο υπεύθυνος μας βρίσκεται δίπλα σας, ειδικά στην κρίσιμη φάση της ανάλυσης και στρατηγικού σχεδιασμού.',
    
    
    // Contact Section
    'contact.title': 'Επικοινωνήστε μαζί μας',
    'contact.follow': 'Ακολουθήστε μας',
    'contact.ready.title': 'Έτοιμοι να Ξεκινήσουμε;',
    'contact.ready.description': 'Επικοινωνήστε μαζί μας σήμερα και ας συζητήσουμε πώς μπορούμε να βοηθήσουμε την επιχείρησή σας να αναπτυχθεί στο διαδίκτυο.',
    'contact.direct': 'Ή επικοινωνήστε απευθείας:',
    
    // Video Section
    'video.title': 'Δείτε τη Δουλειά μας σε Δράση',
    'video.description': 'Παρακολουθήστε πώς βοηθάμε τις τοπικές επιχειρήσεις να μεταμορφώσουν την ψηφιακή τους παρουσία και να αυξήσουν την πελατειακή τους βάση.',
    
    // CTA Section
    'cta.title': 'Δεν είστε σίγουροι ποιες υπηρεσίες χρειάζεστε;',
    'cta.description': 'Κάθε επιχείρηση είναι μοναδική. Ας συζητήσουμε τους στόχους σας και θα σας προτείνουμε την καλύτερη στρατηγική για την ψηφιακή σας ανάπτυξη.',
    'cta.consultation': 'Δωρεάν Συμβουλευτική - 30 λεπτά',
    'cta.call': 'Καλέστε μας τώρα',
    'cta.note': 'Χωρίς δέσμευση • Απαντάμε εντός 24 ωρών',
    
    // Common
    'common.back': 'Πίσω',
    'common.loading': 'Φόρτωση...',
    'common.error': 'Σφάλμα',
    'common.success': 'Επιτυχία',
    
    // Projects - Individual project content
    'projects.voltbuild.title': 'VoltBuild – EV Charging Experts',
    'projects.voltbuild.description': 'voltbuild.gr - Κατασκευή Ιστοσελίδας · E-shop · SEO & Περιεχόμενο · Στρατηγική',
    'projects.voltbuild.tags.website': 'Κατασκευή Ιστοσελίδας',
    'projects.voltbuild.tags.eshop': 'E-shop',
    'projects.voltbuild.tags.seo': 'SEO & Περιεχόμενο',
    'projects.voltbuild.tags.strategy': 'Στρατηγική',
    
    'projects.naterra.title': 'Naterra – Home Renovations (UK)',
    'projects.naterra.description': 'naterra.uk - Branding · Κατασκευή Ιστοσελίδας · Digital Marketing Strategy',
    'projects.naterra.tags.branding': 'Branding',
    'projects.naterra.tags.website': 'Κατασκευή Ιστοσελίδας',
    'projects.naterra.tags.marketing': 'Digital Marketing Strategy',
    
    'projects.kts.title': 'KTS Woodart – Custom Carpentry (UK)',
    'projects.kts.description': 'ktswoodart.com - Κατασκευή Ιστοσελίδας · Rebranding · Digital Strategy · Social Media',
    'projects.kts.tags.website': 'Κατασκευή Ιστοσελίδας',
    'projects.kts.tags.rebranding': 'Rebranding',
    'projects.kts.tags.digital': 'Digital Strategy',
    'projects.kts.tags.social': 'Social Media',
    
    'projects.antonistagiannis.title': 'AntonisTagiannis.gr – Premium Portfolio Ιστοσελίδα',
    'projects.antonistagiannis.description': 'antonistagiannis.gr - Premium Portfolio Ιστοσελίδα · Πολιτικός Μηχανικός · WordPress Development',
    'projects.antonistagiannis.tags.portfolio': 'Premium Portfolio',
    'projects.antonistagiannis.tags.engineering': 'Πολιτικός Μηχανικός',
    'projects.antonistagiannis.tags.wordpress': 'WordPress Ανάπτυξη',
    'projects.antonistagiannis.tags.seo': 'SEO-ready Δομή',
    
    'projects.pieriastrom.title': 'Pieria Strom – Dynamic eCommerce Platform',
    'projects.pieriastrom.description': 'Η Pieria Strom είναι premium κατασκευαστής στρωμάτων στην Ελλάδα. Δημιουργήσαμε custom WooCommerce πλατφόρμα με δυναμική τιμολόγηση, προηγμένη διαμόρφωση προϊόντων και απόδοση που κλιμακώνεται.',
    'projects.pieriastrom.tags.ecommerce': 'Τύπος: Custom eCommerce Website',
    'projects.pieriastrom.tags.wordpress': 'Κλάδος: Λιανική / Βιοτεχνία (Στρώματα & Λευκά είδη)',
    'projects.pieriastrom.tags.woocomerce': 'Πλατφόρμα: WordPress & WooCommerce',
    'projects.pieriastrom.tags.custom': 'URL: shop.pieriastrom.gr',
    
    'projects.tsiouchas.title': 'Tsiouchas – Custom One-Page Website & Lead Capture System',
    'projects.tsiouchas.description': 'tsiouchas.com - Custom WordPress One-Page Website · Branding · Lead Generation · Automation',
    'projects.tsiouchas.tags.wordpress': 'WordPress',
    'projects.tsiouchas.tags.onepage': 'One-Page Website',
    'projects.tsiouchas.tags.lead': 'Lead Capture',
    'projects.tsiouchas.tags.automation': 'Automation',
    'projects.tsiouchas.tags.plugin': 'Custom Plugin',
    
    'projects.sidiropoulos.title': 'Sidiropoulos Clinic – Ουρολογικό Ιατρείο',
    'projects.sidiropoulos.description': 'sidiropoulos-urology.gr - Κατασκευή Ιστοσελίδας · Ιατρικό Περιεχόμενο · Δομή για SEO',
    'projects.sidiropoulos.tags.website': 'Κατασκευή Ιστοσελίδας',
    'projects.sidiropoulos.tags.medical': 'Ιατρικό Περιεχόμενο',
    'projects.sidiropoulos.tags.seo': 'Δομή για SEO',
    
    'projects.underground.title': 'Underground Talent – Music Media Brand (UK)',
    'projects.underground.description': 'undergroundtalent.co.uk - Κατασκευή Ιστοσελίδας · Branding · 360° Στρατηγική Marketing · Σχεδιασμός Προϊόντων',
    'projects.underground.tags.website': 'Κατασκευή Ιστοσελίδας',
    'projects.underground.tags.branding': 'Branding',
    'projects.underground.tags.marketing360': '360° Marketing',
    'projects.underground.tags.products': 'Σχεδιασμός Προϊόντων',

    'projects.lensory.title': 'Lensory – Creative Photography Studio',
    'projects.lensory.description': 'lensory.co - Κατασκευή Ιστοσελίδας · Branding · SEO Βελτιστοποίηση',
    'projects.lensory.tags.website': 'Κατασκευή Ιστοσελίδας',
    'projects.lensory.tags.branding': 'Branding',
    'projects.lensory.tags.seo': 'SEO Βελτιστοποίηση',

    'projects.invoicedirect.title': 'InvoiceDirect – SaaS Invoicing Platform',
    'projects.invoicedirect.description': 'invoice.direct - SaaS Web Application · Branding · Web App Development',
    'projects.invoicedirect.tags.saas': 'SaaS Web Application',
    'projects.invoicedirect.tags.branding': 'Branding',
    'projects.invoicedirect.tags.webapp': 'Web App Development',

    'projects.gkoutzioulis.title': 'Ioannis Gkoutzioulis – Web Developer & Music Producer',
    'projects.gkoutzioulis.description': 'ioannis-gkoutzioulis.com - Portfolio Website · Personal Branding · Responsive Design',
    'projects.gkoutzioulis.tags.portfolio': 'Portfolio Website',
    'projects.gkoutzioulis.tags.branding': 'Personal Branding',
    'projects.gkoutzioulis.tags.responsive': 'Responsive Design',

    'projects.tradesignalx.title': 'TradeSignalX – Trading Community Platform',
    'projects.tradesignalx.description': 'tradesignalx.com - One Page Website · Frontend Development · Performance Optimization',
    'projects.tradesignalx.tags.website': 'One Page Website',
    'projects.tradesignalx.tags.frontend': 'Frontend Development',
    'projects.tradesignalx.tags.performance': 'Performance Optimization',
    
    'projects.buttons.more': 'Δείτε Περισσότερα',
    'projects.buttons.details': 'Λεπτομέρειες Έργου',
    'projects.buttons.less': 'Εμφάνιση Λιγότερων',
    'projects.comingSoon': 'Σύντομα',
    'services.buttons.learnMore': 'Μάθετε Περισσότερα',
    'projects.challenge': 'Η Πρόκληση',
    'projects.solution': 'Η Λύση μας',
    'projects.results': 'Αποτελέσματα',
    
    // Pricing - Package features and details
    'pricing.starter.description': 'Για επιχειρήσεις που ξεκινούν την online προβολή τους',
    'pricing.growth.description': 'Για επιχειρήσεις που αναπτύσσονται και χρειάζονται σταθερή ψηφιακή παρουσία',
    'pricing.dominance.description': 'Για επιχειρήσεις που θέλουν να κυριαρχήσουν στον κλάδο τους',
    
    'pricing.features.website': 'Κατασκευή ιστοτόπου (έως 6 σελίδες)',
    'pricing.features.hosting': 'Φιλοξενία, ασφάλεια και τεχνική υποστήριξη',
    'pricing.features.seo': 'Βασική βελτιστοποίηση SEO',
    'pricing.features.social1': 'Διαχείριση social media ( 1 πλατφόρμα)',
    'pricing.features.posts8': '8 δημοσιεύσεις / 4 stories / 4 Carousel / 1 Video',
    'pricing.features.campaign1': 'Μία καμπάνια διαφήμισης (Meta ή Google)',
    'pricing.features.blog2': '2 άρθρα blog (SEO optimized) /μήνα',
    'pricing.features.revision1': '1 αναθεώρηση/μήνα',
    'pricing.features.report': 'Μηνιαία αναφορά απόδοσης',
    
    // Growth package features
    'pricing.features.website.unlimited': 'Προσαρμοσμένος ιστότοπος με απεριόριστες σελίδες',
    'pricing.features.seo.enhanced': 'Ενισχυμένο SEO και τοπική παρουσία (GMB, κριτικές)',
    'pricing.features.social2': 'Διαχείριση social media (2 πλατφόρμες)',
    'pricing.features.posts16': '16 δημοσιεύσεις / 8 stories / 8 carousel / 2 videos',
    'pricing.features.campaigns.both': 'Διαφήμιση Meta και Google',
    'pricing.features.strategy': 'Στρατηγική, copywriting, A/B testing',
    'pricing.features.blog4': '4 άρθρα blog (SEO optimized) /μήνα',
    'pricing.features.revision2': '2 αναθεωρήσεις/μήνα',
    
    // Dominance package features
    'pricing.features.website.premium': 'Premium ιστότοπος με προσαρμοσμένες λειτουργίες ή light e-shop',
    'pricing.features.social4': 'Διαχείριση social media (4 πλατφόρμες, περιλαμβάνει Facebook, Instagram, TikTok, LinkedIn)',
    'pricing.features.posts30': '30 δημοσιεύσεις / 15 stories / 15 carousel / 4 videos',
    'pricing.features.blog8': '8 άρθρα blog /μήνα',
    'pricing.features.analytics': 'Λεπτομερής αναφορά KPI & ROI',
    'pricing.features.manager': 'Project manager για συνεχή υποστήριξη',
    
    'pricing.budget.starter': 'από €150 /μήνα',
    'pricing.budget.growth': '€400–€700 /μήνα',
    'pricing.budget.dominance': '€1.000–€2.000+ /μήνα',
    'pricing.budget.note': '(δεν περιλαμβάνεται στο πακέτο)',
    'pricing.budget.recommended': 'Προτεινόμενο διαφημιστικό budget:',
    
    'pricing.note.title': 'Σημαντική Σημείωση',
    'pricing.note.content': 'Το διαφημιστικό budget (Meta ή Google Ads) καταβάλλεται ξεχωριστά και απευθείας στην εκάστοτε πλατφόρμα. Η ομάδα μας αναλαμβάνει τη διαχείριση και τη βελτιστοποίηση των καμπανιών με βάση τους στόχους της επιχείρησής σας.',
    
    'pricing.next.info': 'Επόμενο: Συμπληρώστε μια γρήγορη φόρμα ένταξης για να παράσχετε τις πληροφορίες που χρειαζόμαστε για να ξεκινήσουμε με τις επιλεγμένες υπηρεσίες σας.',
    
    // FAQ Questions and Answers
    'faq.q1': 'Δεν είμαι σίγουρος ποιες ενότητες χρειάζομαι. Μπορείτε να βοηθήσετε;',
    'faq.a1': 'Απολύτως! Προσφέρουμε μια δωρεάν συμβουλή για να συζητήσουμε τους επιχειρηματικούς σας στόχους και να προτείνουμε τις καλύτερες ενότητες για την επίτευξή τους. Θα αναλύσουμε την τρέχουσα διαδικτυακή σας παρουσία, θα εντοπίσουμε ευκαιρίες βελτίωσης και θα δημιουργήσουμε ένα προσαρμοσμένο σχέδιο προσαρμοσμένο στις συγκεκριμένες ανάγκες σας.',
    
    'faq.q2': 'Τι γίνεται αν χρειαστεί να ακυρώσω τη συνδρομή μου;',
    'faq.a2': 'Μπορείτε να ακυρώσετε τη συνδρομή σας ανά πάσα στιγμή, χωρίς ερωτήσεις. Πιστεύουμε στην απόκτηση της επιχείρησής σας κάθε μήνα και δεν θέλουμε να σας δεσμεύσουμε σε ένα μακροπρόθεσμο συμβόλαιο εάν οι υπηρεσίες μας δεν ανταποκρίνονται στις προσδοκίες σας. Απλώς ειδοποιήστε μας πριν από την επόμενη ημερομηνία χρέωσης και θα επεξεργαστούμε την ακύρωσή σας.',
    
    'faq.q3': 'Υπάρχουν κρυφές χρεώσεις ή μακροπρόθεσμα συμβόλαια;',
    'faq.a3': 'Όχι, δεν υπάρχουν κρυφές χρεώσεις ή μακροπρόθεσμα συμβόλαια. Η τιμολόγησή μας είναι διαφανής και απλή. Η μηνιαία τιμή που βλέπετε είναι η τιμή που πληρώνετε και μπορείτε να ακυρώσετε ανά πάσα στιγμή. Ωστόσο, ενδέχεται να ισχύουν πρόσθετες χρεώσεις για συγκεκριμένες υπηρεσίες — όπως ο διαφημιστικός προϋπολογισμός (δαπάνες διαφήμισης) και η χρήση API τρίτων.',
    
    'faq.q4': 'Πόσο γρήγορα θα δω αποτελέσματα;',
    'faq.a4': 'Το χρονοδιάγραμμα για την εμφάνιση αποτελεσμάτων ποικίλλει ανάλογα με τις ενότητες που επιλέγετε και την τρέχουσα κατάσταση της διαδικτυακής σας παρουσίας. Ωστόσο, οι περισσότεροι πελάτες αρχίζουν να βλέπουν θετικές αλλαγές μέσα στους πρώτους μήνες. Θα παρακολουθούμε στενά την πρόοδό σας και θα παρέχουμε τακτικές αναφορές για να σας κρατάμε ενήμερους.',
    
    'faq.q5': 'Τι γίνεται αν χρειάζομαι υποστήριξη ή έχω ερωτήσεις;',
    'faq.a5': 'Προσφέρουμε αποκλειστική υποστήριξη σε όλους τους πελάτες μας. Μπορείτε να επικοινωνήσετε μαζί μας μέσω email ή τηλεφώνου και θα απαντήσουμε άμεσα για να αντιμετωπίσουμε τις ερωτήσεις και τις ανησυχίες σας. Δεσμευόμαστε να παρέχουμε εξαιρετική εξυπηρέτηση πελατών και να διασφαλίσουμε ότι είστε απόλυτα ικανοποιημένοι με τις υπηρεσίες μας.',
    
    'faq.q6': 'Είμαι κάτοχος του περιεχομένου που δημιουργήθηκε για μένα;',
    'faq.a6': 'Ναι! Διατηρείτε την πλήρη κυριότητα όλου του περιεχομένου που δημιουργήθηκε για εσάς, συμπεριλαμβανομένου του κώδικα ιστότοπου, των σχεδίων, των άρθρων ιστολογίου, των δημοσιεύσεων στα μέσα κοινωνικής δικτύωσης και του διαφημιστικού κειμένου. Πιστεύουμε στην ενδυνάμωση των πελατών μας και στην παροχή ελέγχου των ψηφιακών τους στοιχείων.',
    
    // Project Details - Individual project content
    'projects.voltbuild.challenge': 'Η VoltBuild χρειαζόταν έναν σύγχρονο ιστότοπο με SEO-first δομή για να ξεχωρίσει στον ταχέως αναπτυσσόμενο χώρο των φορτιστών EV στην Ελλάδα.',
    'projects.voltbuild.solution': 'Κατασκευάσαμε responsive website με λειτουργικό eshop, δημιουργήσαμε SEO σελίδες υπηρεσιών και αναπτύξαμε περιεχόμενο για οργανική ανάπτυξη.',
    'projects.voltbuild.result': 'Η VoltBuild ξεχώρισε στον ταχέως αναπτυσσόμενο χώρο των φορτιστών EV στην Ελλάδα',
    'projects.voltbuild.metrics.1': '300% αύξηση οργανικής επισκεψιμότητας',
    'projects.voltbuild.metrics.2': 'Πρώτες θέσεις στο Google για βασικές λέξεις-κλειδιά',
    'projects.voltbuild.metrics.3': 'Αύξηση εισερχόμενων leads και πωλήσεων',
    
    'projects.naterra.challenge': 'Η Naterra ήθελε να ενισχύσει την παρουσία της στο Λονδίνο και να ξεχωρίσει στον κλάδο των ανακαινίσεων.',
    'projects.naterra.solution': 'Δημιουργήσαμε brand ταυτότητα, σχεδιάσαμε επαγγελματικό ιστότοπο και βάλαμε τα θεμέλια για στοχευμένη digital στρατηγική.',
    'projects.naterra.result': 'Ενίσχυση της παρουσίας στο Λονδίνο και διαφοροποίηση στον κλάδο των ανακαινίσεων',
    'projects.naterra.metrics.1': 'Σύγχρονη, πλήρως responsive ιστοσελίδα',
    'projects.naterra.metrics.2': 'Σταθερή οργανική ανάπτυξη',
    'projects.naterra.metrics.3': 'Αύξηση αιτήσεων εργασιών μέσω website',
    
    'projects.kts.challenge': 'Η KTS Woodart, με έδρα το Λονδίνο, χρειαζόταν επαναπροσδιορισμό brand και online στρατηγική.',
    'projects.kts.solution': 'Αναπτύξαμε νέα οπτική ταυτότητα, responsive website και πλάνο ψηφιακής ανάπτυξης με εστίαση σε τοπικό κοινό και social media.',
    'projects.kts.result': 'Επαναπροσδιορισμός brand και online στρατηγική με εστίαση σε τοπικό κοινό',
    'projects.kts.metrics.1': 'Ολοκληρωμένο νέο brand με συνεκτική παρουσία',
    'projects.kts.metrics.2': 'Επαγγελματικό portfolio έργων',
    'projects.kts.metrics.3': 'Ενεργοποίηση οργανικής ροής πελατών',
    
    'projects.antonistagiannis.challenge': 'Ο Αντώνης Ταγιάννης χρειαζόταν μια σύγχρονη online παρουσία που να αναδεικνύει την τεχνική του εμπειρία και να εμπνέει εμπιστοσύνη σε έργα μηχανικού.',
    'projects.antonistagiannis.solution': 'Σχεδιάσαμε minimal portfolio με καθαρή αρχιτεκτονική περιεχομένου, αναπτύξαμε WordPress site με βελτιστοποιημένη απόδοση και ενσωματώσαμε υπηρεσίες, πιστοποιήσεις και έργα σε πλήρως responsive εμπειρία.',
    'projects.antonistagiannis.result': 'Επαγγελματική παρουσία που ενισχύει την αξιοπιστία του πολιτικού μηχανικού και προσφέρει γρήγορη, κομψή εμπειρία σε τοπικό κοινό.',
    'projects.antonistagiannis.metrics.1': 'Σαφής δομή υπηρεσιών και έργων με εστίαση στην αξιοπιστία',
    'projects.antonistagiannis.metrics.2': 'WordPress ανάπτυξη με βελτιστοποιημένες ταχύτητες φόρτωσης',
    'projects.antonistagiannis.metrics.3': 'Responsive, SEO-ready εμπειρία για τοπικούς πελάτες',
    
    'projects.tsiouchas.challenge': 'Η επιχείρηση χρειαζόταν μια καθαρή, επαγγελματική one-page παρουσία που να παρουσιάζει ξεκάθαρα την αξία και τα προϊόντα, να μετατρέπει επισκέπτες σε leads και να παραδίδει αυτόματα τον κατάλογο προϊόντων. Η υπάρχουσα προσέγγιση δεν υποστήριζε lead generation ούτε αυτοματισμούς.',
    'projects.tsiouchas.solution': 'Σχεδιάσαμε custom WordPress one-page website, εστιασμένο στη μετατροπή, με custom lead capture plugin για αυτόματη αποστολή email και άμεσο download του καταλόγου. Το σύστημα προσφέρει απλή διαχείριση χωρίς εξαρτήσεις από τρίτα εργαλεία.',
    'projects.tsiouchas.result': 'Ξεκάθαρη παρουσίαση προϊόντων σε μία σελίδα με αυτοματοποιημένη συλλογή leads και άμεση παράδοση καταλόγου χωρίς manual emails.',
    'projects.tsiouchas.metrics.1': 'Αυτοματοποιημένη συλλογή leads',
    'projects.tsiouchas.metrics.2': 'Άμεση παράδοση καταλόγου',
    'projects.tsiouchas.metrics.3': 'Σταθερή βάση για μελλοντική επέκταση',
    
    'projects.sidiropoulos.challenge': 'Το ιατρείο χρειαζόταν σύγχρονη παρουσία που να εμπνέει εμπιστοσύνη και να διευκολύνει την πρόσβαση των ασθενών σε πληροφορίες.',
    'projects.sidiropoulos.solution': 'Δημιουργήσαμε ιστοσελίδα με ξεκάθαρη δομή, ιατρικό περιεχόμενο και ενσωματωμένη φόρμα επικοινωνίας.',
    'projects.sidiropoulos.result': 'Σύγχρονη παρουσία που εμπνέει εμπιστοσύνη και διευκολύνει την πρόσβαση των ασθενών',
    'projects.sidiropoulos.metrics.1': '90% θετικό feedback από χρήστες',
    'projects.sidiropoulos.metrics.2': 'Μείωση ερωτήσεων μέσω τηλεφώνου',
    'projects.sidiropoulos.metrics.3': 'Καλύτερη θέση σε τοπικές αναζητήσεις',
    
    'projects.underground.challenge': 'Το brand ήθελε να ενισχύσει την παρουσία του παγκοσμίως στον χώρο της ηλεκτρονικής μουσικής και των fashion προϊόντων.',
    'projects.underground.solution': 'Δημιουργήσαμε επαγγελματικό website, αναπτύξαμε δυναμική ταυτότητα και εφαρμόσαμε ολιστική στρατηγική marketing με περιεχόμενο, προϊόντα και funnels.',
    'projects.underground.result': 'Ενίσχυση παρουσίας παγκοσμίως στον χώρο της ηλεκτρονικής μουσικής και fashion προϊόντων',
    'projects.underground.metrics.1': '10.000+ επισκέψεις/μήνα',
    'projects.underground.metrics.2': '5.000+ πωλήσεις προϊόντων',
    'projects.underground.metrics.3': 'Top 80 παγκοσμίως στην κατηγορία',
    'projects.underground.metrics.4': 'Ανάπτυξη κοινού μέσω YouTube και direct traffic',

    'projects.lensory.challenge': 'Το photography studio χρειαζόταν έναν μοντέρνο ιστότοπο που να αναδεικνύει τη δουλειά του και να αντικατοπτρίζει το καλλιτεχνικό του όραμα.',
    'projects.lensory.solution': 'Σχεδιάσαμε και αναπτύξαμε έναν κομψό ιστότοπο με WordPress, βελτιώσαμε την εταιρική ταυτότητα και εφαρμόσαμε SEO βελτιστοποίηση για φωτογραφικές λέξεις-κλειδιά.',
    'projects.lensory.result': 'Μινιμαλιστική, γρήγορη και immersive εμπειρία portfolio που αφήνει τα visuals να μιλήσουν μόνα τους',
    'projects.lensory.metrics.1': 'Ενίσχυση αξιοπιστίας και αύξηση ερωτήσεων πελατών',
    'projects.lensory.metrics.2': 'Βελτιστοποίηση για image-heavy σελίδες',
    'projects.lensory.metrics.3': 'Δυνατή online παρουσία και αναγνωρισιμότητα',

    'projects.invoicedirect.challenge': 'Freelancers και μικρές επιχειρήσεις χρειαζόταν μια επαγγελματική πλατφόρμα τιμολόγησης που να είναι εύκολη στη χρήση και να επιτρέπει γρήγορη δημιουργία τιμολογίων.',
    'projects.invoicedirect.solution': 'Αναπτύξαμε SaaS εφαρμογή με custom branding, conversion-optimized σχεδιασμό, front-end με JavaScript/TypeScript και back-end για δυναμική δημιουργία τιμολογίων.',
    'projects.invoicedirect.result': 'Πολυτελής και αξιόπιστη πλατφόρμα τιμολόγησης με conversion-driven εμπειρία',
    'projects.invoicedirect.metrics.1': 'Επαγγελματική εμπειρία τιμολόγησης σε desktop και mobile',
    'projects.invoicedirect.metrics.2': 'Δημιουργία τιμολογίων σε δευτερόλεπτα',
    'projects.invoicedirect.metrics.3': 'Ταχύτερη είσπραξη πληρωμών',

    'projects.gkoutzioulis.challenge': 'Ο Ioannis Gkoutzioulis χρειαζόταν ένα επαγγελματικό portfolio για να παρουσιάσει τις δεξιότητές του ως web developer και music producer.',
    'projects.gkoutzioulis.solution': 'Σχεδιάσαμε custom portfolio με το Uncode theme, καθορίσαμε το brand positioning και βελτιστοποιήσαμε για performance και accessibility.',
    'projects.gkoutzioulis.result': 'Μοντέρνος, responsive ιστότοπος που επικοινωνεί την τεχνική και καλλιτεχνική προσωπικότητα',
    'projects.gkoutzioulis.metrics.1': 'Επαγγελματική ταυτότητα και παρουσίαση έργων',
    'projects.gkoutzioulis.metrics.2': 'Βελτίωση UX για mobile χρήστες',
    'projects.gkoutzioulis.metrics.3': 'Σταθερό foundation για freelance brand',

    'projects.tradesignalx.challenge': 'Το trading community χρειαζόταν έναν ιστότοπο μιας σελίδας σχεδιασμένο για conversion και αύξηση μελών.',
    'projects.tradesignalx.solution': 'Σχεδιάσαμε UX και αρχιτεκτονική για single page, αναπτύξαμε με Astro και JavaScript, και ενσωματώσαμε newsletter και Discord.',
    'projects.tradesignalx.result': 'Καθαρή, γρήγορη και conversion-focused landing εμπειρία για trading community',
    'projects.tradesignalx.metrics.1': 'Αύξηση sign-ups και engagement',
    'projects.tradesignalx.metrics.2': 'Mobile-first βελτιστοποίηση',
    'projects.tradesignalx.metrics.3': 'Θεση ως σύγχρονη και αξιόπιστη κοινότητα',

    'projects.pieriastrom.challenge': 'Η Pieria Strom χρειαζόταν ένα high-performance eCommerce που να εξυπηρετεί ταυτόχρονα λιανική και χονδρική, με παραμετροποίηση προϊόντων σε πραγματικό χρόνο και άψογη εμπειρία WordPress.',
    'projects.pieriastrom.solution': 'Σχεδιάσαμε UI με έμφαση στη σαφήνεια, αναπτύξαμε custom WooCommerce υλοποίηση με plugin για δυναμική τιμολόγηση, και συνδέσαμε διαστάσεις, αποστολές και φόρους σε mobile-first εμπειρία.',
    'projects.pieriastrom.result': 'Υψηλών επιδόσεων eCommerce πλατφόρμα που επιτρέπει διαμόρφωση στρωμάτων σε πραγματικό χρόνο, αυτοματοποιεί την κοστολόγηση και ενισχύει την αξία του brand.',
    'projects.pieriastrom.metrics.1': 'Custom σχεδιασμός ιστοσελίδας με έμφαση στη σαφήνεια και στο mobile-first UX',
    'projects.pieriastrom.metrics.2': 'Dynamic eCommerce ανάπτυξη πάνω σε WooCommerce',
    'projects.pieriastrom.metrics.3': 'Μηχανική WordPress & WooCommerce',
    'projects.pieriastrom.metrics.4': 'Custom plugin για δυναμικό υπολογισμό τιμών και λογική χονδρικής',
    'projects.pieriastrom.metrics.5': 'Προηγμένη ενσωμάτωση αποστολών, φόρων και διαστάσεων προϊόντων',
    'projects.pieriastrom.metrics.6': 'SEO-ready δομή με ρυθμίσεις analytics',
    
    // Footer translations
    'footer.company.description': 'Μετατρέπουμε ιδέες σε ψηφιακή πραγματικότητα με ουσιαστικό σχεδιασμό και σωστή στρατηγική.',
    'footer.services.title': 'Υπηρεσίες',
    'footer.services.website': 'Κατασκευή Ιστοσελίδων',
    'footer.services.uiux': 'Branding',
    'footer.services.ecommerce': 'Κατασκευή E-shop',
    'footer.services.marketing': 'Marketing',
    'footer.services.mobile': 'Web & Mobile Apps',
    'footer.services.content': 'Παραγωγή Περιεχομένου',
    'footer.links.title': 'Γρήγοροι Σύνδεσμοι',
    'footer.links.home': 'Αρχική',
    'footer.links.about': 'Σχετικά με Εμάς',
    'footer.links.services': 'Υπηρεσίες',
    'footer.links.projects': 'Έργα',
    'footer.links.articles': 'Άρθρα',
    'footer.links.pricing': 'Τιμές',
    'footer.links.contact': 'Επικοινωνία',
    'footer.links.privacy': 'Πολιτική Απορρήτου',
    'footer.links.cookies': 'Πολιτική Cookies',
    'footer.contact.title': 'Επικοινωνήστε μαζί μας',
    'footer.copyright': 'Με επιφύλαξη παντός δικαιώματος.',
    
    // Contact page specific
    'contact.scroll.top': 'Πάμε στην κορυφή',
    
    // Consultation form translations
    'consultation.title': 'Δωρεάν Συμβουλευτική',
    'consultation.back': 'Πίσω',
    'consultation.form.title': 'Ας Γνωριστούμε!',
    'consultation.form.description': 'Συμπληρώστε τη φόρμα παρακάτω και θα επικοινωνήσουμε μαζί σας εντός 24 ωρών για να συζητήσουμε τις ανάγκες της επιχείρησής σας.',
    'consultation.form.name': 'Όνομα',
    'consultation.form.name.placeholder': 'Το όνομά σας',
    'consultation.form.email': 'Email',
    'consultation.form.email.placeholder': 'email@example.com',
    'consultation.form.phone': 'Τηλέφωνο',
    'consultation.form.phone.placeholder': '+30 210 1234567',
    'consultation.form.company': 'Επιχείρηση',
    'consultation.form.company.placeholder': 'Όνομα επιχείρησης',
    'consultation.form.website': 'Υπάρχων Ιστότοπος',
    'consultation.form.website.placeholder': 'https://www.example.com',
    'consultation.form.services': 'Ποιες υπηρεσίες σας ενδιαφέρουν;',
    'consultation.form.budget': 'Προϋπολογισμός',
    'consultation.form.budget.placeholder': 'Επιλέξτε προϋπολογισμό',
    'consultation.form.timeline': 'Χρονοδιάγραμμα',
    'consultation.form.timeline.placeholder': 'Επιλέξτε χρονοδιάγραμμα',
    
    // Service options for consultation form
    'consultation.services.website': 'Κατασκευή Ιστοσελίδων',
    'consultation.services.uiux': 'Σχεδιασμός UI/UX',
    'consultation.services.ecommerce': 'Λύσεις E-Commerce',
    'consultation.services.marketing': 'Ψηφιακό Μάρκετινγκ',
    'consultation.services.seo': 'Βελτιστοποίηση SEO',
    'consultation.services.social': 'Διαχείριση Social Media',
    'consultation.services.mobile': 'Ανάπτυξη Mobile App',
    'consultation.services.branding': 'Branding & Σχεδιασμός Logo',
    'consultation.services.content': 'Δημιουργία Περιεχομένου',
    'consultation.services.email': 'Email Marketing',
    
    // Budget options
    'consultation.budget.under1k': 'Κάτω από €1.000',
    'consultation.budget.1k3k': '€1.000 - €3.000',
    'consultation.budget.3k5k': '€3.000 - €5.000',
    'consultation.budget.5k10k': '€5.000 - €10.000',
    'consultation.budget.over10k': 'Πάνω από €10.000',
    'consultation.budget.discuss': 'Θα συζητήσουμε',
    
    // Timeline options
    'consultation.timeline.immediate': 'Άμεσα (1-2 εβδομάδες)',
    'consultation.timeline.soon': 'Σύντομα (1 μήνας)',
    'consultation.timeline.medium': 'Μεσοπρόθεσμα (2-3 μήνες)',
    'consultation.timeline.long': 'Μακροπρόθεσμα (3+ μήνες)',
    'consultation.timeline.flexible': 'Ευέλικτο χρονοδιάγραμμα',
    
    // Consultation process section
    'consultation.process.title': 'Τι να περιμένετε;',
    'consultation.process.step1.title': 'Ανάλυση Αναγκών',
    'consultation.process.step1.description': 'Συζητάμε τους στόχους και τις προκλήσεις σας',
    'consultation.process.step2.title': 'Προτάσεις Λύσεων',
    'consultation.process.step2.description': 'Παρουσιάζουμε προσαρμοσμένες στρατηγικές',
    'consultation.process.step3.title': 'Σχέδιο Δράσης',
    'consultation.process.step3.description': 'Δημιουργούμε ένα σαφές roadmap για την επιτυχία',
    'consultation.form.message': 'Πείτε μας περισσότερα για το έργο σας',
    'consultation.form.message.placeholder': 'Περιγράψτε τους στόχους σας, τις προκλήσεις που αντιμετωπίζετε, ή οτιδήποτε άλλο θα θέλατε να μας πείτε...',
    'consultation.form.submit': 'Στείλτε την Αίτηση',
    'consultation.form.submitting': 'Αποστολή...',
    'consultation.form.required': 'Υποχρεωτικά πεδία',
    'consultation.step.contact': 'Επικοινωνία',
    'consultation.step.services': 'Υπηρεσίες',
    'consultation.step.details': 'Λεπτομέρειες',
    'consultation.step.message': 'Μήνυμα',

    // Tetris game translations
    'tetris.title': 'Παιχνίδι',
    'tetris.back': 'Πίσω',
    'tetris.score': 'Σκορ',
    'tetris.level': 'Επίπεδο',
    'tetris.lines': 'Γραμμές',
    'tetris.pause': 'Παύση',
    'tetris.continue': 'Συνέχεια Παιχνιδιού',
    'tetris.gameover': 'Τέλος Παιχνιδιού!',
    'tetris.finalscore': 'Τελικό Σκορ:',
    'tetris.newgame': 'Νέο Παιχνίδι',
    'tetris.quickdrop': 'Γρήγορη Πτώση',

    // Checklist Lead Magnet
    'checklist.eyebrow': 'Δωρεάν PDF Checklist',
    'checklist.title': 'Δωρεάν Checklist Επαγγελματικής Ιστοσελίδας',
    'checklist.subtitle': 'Δες σε 10 λεπτά αν η ιστοσελίδα σου σου κοστίζει πελάτες',
    'checklist.bulletsLead': 'Με αυτό το checklist θα:',
    'checklist.bullets': [
      'Δες τι λείπει από το site σου για να φέρνει περισσότερες κλήσεις & αιτήματα',
      'Ελέγξεις βασικά σημεία: mobile, ταχύτητα, SEO και επικοινωνία',
      'Πάρεις μια ξεκάθαρη λίστα ενεργειών για άμεσες βελτιώσεις'
    ],
    'checklist.hero.badges': ['10 λεπτά', '5 κρίσιμες ενότητες', 'Action list'],
    'checklist.previewNote': 'Μια σύντομη, πρακτική λίστα ελέγχου για να αξιολογήσεις γρήγορα την αποτελεσματικότητα του site σου.',
    'checklist.form.title': 'Κατέβασε το δωρεάν PDF',
    'checklist.form.subtitle': 'Συμπλήρωσε τα στοιχεία σου για άμεση πρόσβαση στο δωρεάν checklist.',
    'checklist.form.nameLabel': 'Όνομα',
    'checklist.form.emailLabel': 'Email',
    'checklist.form.namePlaceholder': 'Το όνομά σου',
    'checklist.form.emailPlaceholder': 'Το email σου',
    'checklist.form.submit': 'Συνέχεια',
    'checklist.form.loading': 'Αποστολή...',
    'checklist.form.privacy': 'Δεν κάνουμε spam. Θα λάβεις 1–2 χρήσιμα emails.',
    'checklist.form.nameError': 'Το όνομα είναι υποχρεωτικό.',
    'checklist.form.emailError': 'Το email είναι υποχρεωτικό.',
    'checklist.form.emailInvalid': 'Παρακαλώ εισάγετε έγκυρο email.',
    'checklist.form.submitError': 'Κάτι πήγε στραβά. Δοκίμασε ξανά σε λίγο.',
    'checklist.included.title': 'Τι περιλαμβάνει το checklist',
    'checklist.included.subtitle': '',
    'checklist.included.items.0.title': 'Ταχύτητα & Απόδοση',
    'checklist.included.items.0.desc': 'Σημεία που καθυστερούν την ιστοσελίδα σου και μειώνουν την εμπιστοσύνη του επισκέπτη.',
    'checklist.included.items.1.title': 'Mobile εμπειρία',
    'checklist.included.items.1.desc': 'Έλεγχος αν το site σου δουλεύει σωστά και ευχάριστα σε κινητά.',
    'checklist.included.items.2.title': 'SEO & Επικοινωνία',
    'checklist.included.items.2.desc': 'Βασικά στοιχεία για να σε βρίσκουν στο Google και να επικοινωνούν μαζί σου.',
    'checklist.steps.title': 'Πώς δουλεύει',
    'checklist.steps.subtitle': 'Απλό. Γρήγορο. Χρήσιμο.',
    'checklist.steps.items.0.title': 'Κατέβασε',
    'checklist.steps.items.0.desc': 'Πάρε το PDF άμεσα μετά τη φόρμα.',
    'checklist.steps.items.1.title': 'Έλεγξε',
    'checklist.steps.items.1.desc': 'Βλέπεις τι υπάρχει και τι λείπει από την ιστοσελίδα σου.',
    'checklist.steps.items.2.title': 'Βελτίωσε',
    'checklist.steps.items.2.desc': 'Ακολουθείς τη λίστα ενεργειών για άμεσες αλλαγές.',
    'checklist.side.eyebrow': 'ΤΙ ΘΑ ΚΕΡΔΙΣΕΙΣ',
    'checklist.side.title': 'Τι θα κερδίσεις',
    'checklist.side.description': 'Μια καθαρή, εφαρμόσιμη αξιολόγηση με προτεραιότητες.',
    'checklist.side.highlights': [
      'Έτοιμο checklist για γρήγορο έλεγχο',
      'Εστίαση σε conversions και lead generation',
      'Σαφή σημεία βελτίωσης χωρίς τεχνική ορολογία'
    ],
    'checklist.thankYou.eyebrow': 'ΕΤΟΙΜΟ!',
    'checklist.thankYou.title': 'Έτοιμο!',
    'checklist.thankYou.subtitle': 'Κατέβασε το PDF checklist',
    'checklist.thankYou.download': 'Λήψη PDF',
    'checklist.thankYou.secondaryCta': 'Ζήτησε Δωρεάν Έλεγχο Ιστοσελίδας',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.about': 'About',
    'nav.projects': 'Projects',
    'nav.pricing': 'Pricing',
    'nav.contact': 'Contact',
    'nav.consultation': 'Free Consultation',

    // Hero Section
    'hero.title.part1': 'We Help Local Businesses ',
    'hero.title.part2': 'Grow Online',
    'hero.description': 'We create websites and strategic marketing that brings new customers, strengthens your image, and turns your online presence into a business advantage.',
    'hero.cta.consultation': 'Free Consultation',
    'hero.cta.game': 'Waste Your Time',

    // Services Section
    'services.title': 'How We Help Local Businesses',
    'services.description': 'We offer specialized services designed to help your business grow online.',
    'services.website.title': 'Website Development',
    'services.website.description': 'We build professional websites that load fast, work perfectly on mobile, and are designed to convert visitors into customers.',
    'services.eshop.title': 'E-shop Development',
    'services.eshop.description': 'We set up modern online stores, secure and easy to manage, with payment, shipping, and inventory features. We help you sell online to a wider audience.',
    'services.branding.title': 'Branding',
    'services.branding.description': 'We create comprehensive corporate identities with emphasis on consistency, recognition, and strategic differentiation of your business.',
    'services.marketing.title': 'Marketing Strategies',
    'services.marketing.description': 'We design and implement marketing strategies that deliver measurable results — from SEO and Google Ads to organic content and social media advertising.',
    'services.mobile.title': 'Mobile App Development',
    'services.mobile.description': 'We develop mobile apps for businesses that want to offer services, bookings, or products through mobile, with user-friendly design and fast response.',
    'services.content.title': 'Content Production',
    'services.content.description': 'We produce professional content, such as videos, graphics, and photos, that highlight your business and strengthen your online image.',

    // Consultation form translations
    'consultation.title': 'Free Consultation',
    'consultation.title.label': 'Let\'s Talk',
    'consultation.back': 'Back',
    'consultation.form.title': 'Let\'s Connect!',
    'consultation.form.description': 'Fill out the form below and we\'ll contact you within 24 hours to discuss your business needs.',
    'consultation.form.name': 'Name',
    'consultation.form.name.placeholder': 'Your name',
    'consultation.form.email': 'Email',
    'consultation.form.email.placeholder': 'email@example.com',
    'consultation.form.phone': 'Phone',
    'consultation.form.phone.placeholder': '+44 20 1234 5678',
    'consultation.form.company': 'Company',
    'consultation.form.company.placeholder': 'Company name',
    'consultation.form.website': 'Current Website',
    'consultation.form.website.placeholder': 'https://www.example.com',
    'consultation.form.services': 'Which services are you interested in?',
    'consultation.form.budget': 'Budget',
    'consultation.form.budget.placeholder': 'Select a budget',
    'consultation.form.timeline': 'Timeline',
    'consultation.form.timeline.placeholder': 'Select a timeline',
    
    // Service options for consultation form
    'consultation.services.website': 'Website Development',
    'consultation.services.uiux': 'UI/UX Design',
    'consultation.services.ecommerce': 'E-commerce Solutions',
    'consultation.services.marketing': 'Digital Marketing',
    'consultation.services.seo': 'SEO Optimization',
    'consultation.services.social': 'Social Media Management',
    'consultation.services.mobile': 'Mobile App Development',
    'consultation.services.branding': 'Branding & Logo Design',
    'consultation.services.content': 'Content Creation',
    'consultation.services.email': 'Email Marketing',
    
    // Budget options
    'consultation.budget.under1k': 'Under €1.000',
    'consultation.budget.1k3k': '€1.000 - €3.000',
    'consultation.budget.3k5k': '€3.000 - €5.000',
    'consultation.budget.5k10k': '€5.000 - €10.000',
    'consultation.budget.over10k': 'Over €10.000',
    'consultation.budget.discuss': 'Let\'s discuss',
    
    // Timeline options
    'consultation.timeline.immediate': 'Immediately (1-2 weeks)',
    'consultation.timeline.soon': 'Soon (1 month)',
    'consultation.timeline.medium': 'Medium term (2-3 months)',
    'consultation.timeline.long': 'Long term (3+ months)',
    'consultation.timeline.flexible': 'Flexible timeline',
    
    // Consultation process section
    'consultation.process.title': 'What to Expect?',
    'consultation.process.step1.title': 'Needs Analysis',
    'consultation.process.step1.description': 'We discuss your goals and challenges',
    'consultation.process.step2.title': 'Solution Proposals',
    'consultation.process.step2.description': 'We present tailored strategies',
    'consultation.process.step3.title': 'Action Plan',
    'consultation.process.step3.description': 'We create a clear roadmap for success',
    'consultation.form.message': 'Tell us more about your project',
    'consultation.form.message.placeholder': 'Describe your goals, challenges, or anything else you\'d like us to know...',
    'consultation.form.submit': 'Send Request',
    'consultation.form.submitting': 'Sending...',
    'consultation.form.required': '* Required fields',
    'consultation.step.contact': 'Contact',
    'consultation.step.services': 'Services',
    'consultation.step.details': 'Details',
    'consultation.step.message': 'Message',

    // Footer translations
    'footer.company.description': 'We turn ideas into digital reality with thoughtful design and clear strategy.',
    'footer.services.title': 'Services',
    'footer.services.website': 'Web Design & Development',
    'footer.services.uiux': 'Branding',
    'footer.services.ecommerce': 'E-commerce',
    'footer.services.marketing': 'Marketing',
    'footer.services.mobile': 'Web & Mobile Apps',
    'footer.services.content': 'Content Production',
    'footer.links.title': 'Quick Links',
    'footer.links.home': 'Home',
    'footer.links.about': 'About',
    'footer.links.projects': 'Projects',
    'footer.links.articles': 'Articles',
    'footer.links.contact': 'Contact',
    'footer.links.privacy': 'Privacy Policy',
    'footer.links.cookies': 'Cookie Policy',
    'footer.contact.title': 'Get in touch',
    'footer.copyright': 'All rights reserved.',

    // Projects Section
    'projects.title': 'Projects & Case Studies',
    'projects.description': 'See how we helped businesses in Greece and internationally grow digitally',

    // Projects - Individual project content
    'projects.voltbuild.title': 'VoltBuild – EV Charging Experts',
    'projects.voltbuild.description': 'voltbuild.gr - Website Development · E-shop · SEO & Content · Strategy',
    'projects.voltbuild.tags.website': 'Website Development',
    'projects.voltbuild.tags.eshop': 'E-shop',
    'projects.voltbuild.tags.seo': 'SEO & Content',
    'projects.voltbuild.tags.strategy': 'Strategy',

    'projects.naterra.title': 'Naterra – Home Renovations (UK)',
    'projects.naterra.description': 'naterra.uk - Branding · Website Development · Digital Marketing Strategy',
    'projects.naterra.tags.branding': 'Branding',
    'projects.naterra.tags.website': 'Website Development',
    'projects.naterra.tags.marketing': 'Digital Marketing Strategy',

    'projects.kts.title': 'KTS Woodart – Custom Carpentry (UK)',
    'projects.kts.description': 'ktswoodart.com - Website Development · Rebranding · Digital Strategy · Social Media',
    'projects.kts.tags.website': 'Website Development',
    'projects.kts.tags.rebranding': 'Rebranding',
    'projects.kts.tags.digital': 'Digital Strategy',
    'projects.kts.tags.social': 'Social Media',
    'projects.antonistagiannis.title': 'AntonisTagiannis.gr – Premium Portfolio Website',
    'projects.antonistagiannis.description': 'antonistagiannis.gr - Premium Portfolio Website · Civil Engineering · WordPress Development',
    'projects.antonistagiannis.tags.portfolio': 'Premium Portfolio Website',
    'projects.antonistagiannis.tags.engineering': 'Civil Engineering',
    'projects.antonistagiannis.tags.wordpress': 'WordPress Development',
    'projects.antonistagiannis.tags.seo': 'SEO-Ready Layout',
    
    'projects.sidiropoulos.title': 'Sidiropoulos Clinic – Urology Clinic',
    'projects.sidiropoulos.description': 'sidiropoulos-urology.gr - Website Development · Medical Content · SEO Structure',
    'projects.sidiropoulos.tags.website': 'Website Development',
    'projects.sidiropoulos.tags.medical': 'Medical Content',
    'projects.sidiropoulos.tags.seo': 'SEO Structure',

    'projects.underground.title': 'Underground Talent – Music Media Brand (UK)',
    'projects.underground.description': 'undergroundtalent.co.uk - Website Development · Branding · 360° Marketing Strategy · Product Design',
    'projects.underground.tags.website': 'Website Development',
    'projects.underground.tags.branding': 'Branding',
    'projects.underground.tags.marketing360': '360° Marketing',
    'projects.underground.tags.products': 'Product Design',

    'projects.lensory.title': 'Lensory – Creative Photography Studio',
    'projects.lensory.description': 'lensory.co - Website Development · Branding · SEO Optimization',
    'projects.lensory.tags.website': 'Website Development',
    'projects.lensory.tags.branding': 'Branding',
    'projects.lensory.tags.seo': 'SEO Optimization',

    'projects.invoicedirect.title': 'InvoiceDirect – SaaS Invoicing Platform',
    'projects.invoicedirect.description': 'invoice.direct - SaaS Web Application · Branding · Web App Development',
    'projects.invoicedirect.tags.saas': 'SaaS Web Application',
    'projects.invoicedirect.tags.branding': 'Branding',
    'projects.invoicedirect.tags.webapp': 'Web App Development',

    'projects.gkoutzioulis.title': 'Ioannis Gkoutzioulis – Web Developer & Music Producer',
    'projects.gkoutzioulis.description': 'ioannis-gkoutzioulis.com - Portfolio Website · Personal Branding · Responsive Design',
    'projects.gkoutzioulis.tags.portfolio': 'Portfolio Website',
    'projects.gkoutzioulis.tags.branding': 'Personal Branding',
    'projects.gkoutzioulis.tags.responsive': 'Responsive Design',

    'projects.tradesignalx.title': 'TradeSignalX – Trading Community Platform',
    'projects.tradesignalx.description': 'tradesignalx.com - One Page Website · Frontend Development · Performance Optimization',
    'projects.tradesignalx.tags.website': 'One Page Website',
    'projects.tradesignalx.tags.frontend': 'Frontend Development',
    'projects.tradesignalx.tags.performance': 'Performance Optimization',

    'projects.pieriastrom.title': 'Pieria Strom – Dynamic eCommerce Platform',
    'projects.pieriastrom.description': 'Pieria Strom is a premium mattress manufacturer in Greece. We delivered a custom WooCommerce platform with dynamic pricing, advanced product configuration, and performance built to scale.',
    'projects.pieriastrom.tags.ecommerce': 'Type: Custom eCommerce Website',
    'projects.pieriastrom.tags.wordpress': 'Industry: Retail / Manufacturing (Mattresses & Bedding)',
    'projects.pieriastrom.tags.woocomerce': 'Platform: WordPress & WooCommerce',
    'projects.pieriastrom.tags.custom': 'URL: shop.pieriastrom.gr',

    'projects.tsiouchas.title': 'Tsiouchas – Custom One-Page Website & Lead Capture System',
    'projects.tsiouchas.description': 'tsiouchas.com - Custom WordPress One-Page Website · Branding · Lead Generation · Automation',
    'projects.tsiouchas.tags.wordpress': 'WordPress',
    'projects.tsiouchas.tags.onepage': 'One-Page Website',
    'projects.tsiouchas.tags.lead': 'Lead Capture',
    'projects.tsiouchas.tags.automation': 'Automation',
    'projects.tsiouchas.tags.plugin': 'Custom Plugin',

    'projects.buttons.more': 'Learn More',
    'projects.buttons.details': 'Project Details',
    'projects.buttons.less': 'Show Less',
    'projects.comingSoon': 'Coming Soon',
    'services.buttons.learnMore': 'Learn More',
    'projects.challenge': 'The Challenge',
    'projects.solution': 'Our Solution',
    'projects.results': 'Results',

    // Project Details - Individual project content
    'projects.antonistagiannis.challenge': 'Antonis Tagiannis needed a modern portfolio that showcases his engineering expertise and builds instant trust with construction clients.',
    'projects.antonistagiannis.solution': 'We crafted a minimal WordPress experience with clear content architecture, performance optimizations, and integrated service descriptions, certifications, and project highlights.',
    'projects.antonistagiannis.result': 'Refined professional presence that positions Antonis as a reliable civil engineer and delivers a fast, elegant experience for local audiences.',
    'projects.antonistagiannis.metrics.1': 'Structured service and project presentation focused on credibility',
    'projects.antonistagiannis.metrics.2': 'Fast-loading WordPress build with performance optimization',
    'projects.antonistagiannis.metrics.3': 'Responsive, SEO-ready layout tailored to civil engineering services',
    
    'projects.voltbuild.challenge': 'VoltBuild needed a modern website with SEO-first structure to stand out in the rapidly growing EV charging space in Greece.',
    'projects.voltbuild.solution': 'We built a responsive website with a functional eshop, created SEO service pages, and developed content for organic growth.',
    'projects.voltbuild.result': 'VoltBuild stood out in the rapidly growing EV charging space in Greece',
    'projects.voltbuild.metrics.1': '300% increase in organic traffic',
    'projects.voltbuild.metrics.2': 'Top positions on Google for key keywords',
    'projects.voltbuild.metrics.3': 'Increase in incoming leads and sales',

    'projects.naterra.challenge': 'Naterra wanted to strengthen its presence in London and stand out in the renovation sector.',
    'projects.naterra.solution': 'We created brand identity, designed a professional website, and laid the foundation for targeted digital strategy.',
    'projects.naterra.result': 'Strengthened presence in London and differentiation in the renovation sector',
    'projects.naterra.metrics.1': 'Modern, fully responsive website',
    'projects.naterra.metrics.2': 'Steady organic growth',
    'projects.naterra.metrics.3': 'Increase in job requests through website',

    'projects.kts.challenge': 'KTS Woodart, based in London, needed brand redefinition and online strategy.',
    'projects.kts.solution': 'We developed new visual identity, responsive website, and digital growth plan focused on local audience and social media.',
    'projects.kts.result': 'Brand redefinition and online strategy focused on local audience',
    'projects.kts.metrics.1': 'Complete new brand with consistent presence',
    'projects.kts.metrics.2': 'Professional portfolio of work',
    'projects.kts.metrics.3': 'Activation of organic customer flow',

    'projects.tsiouchas.challenge': 'The business needed a clean, professional one-page presence that clearly presents value and products, converts visitors into leads, and automatically delivers the product catalog. The existing approach did not support lead generation or automation.',
    'projects.tsiouchas.solution': 'We designed a custom WordPress one-page website focused on conversion, with a custom lead capture plugin for automated email sending and instant catalog download. The system offers simple management without reliance on third-party tools.',
    'projects.tsiouchas.result': 'Clear product presentation on one page with automated lead collection and instant catalogue delivery without manual emails.',
    'projects.tsiouchas.metrics.1': 'Automated lead collection',
    'projects.tsiouchas.metrics.2': 'Instant catalog delivery',
    'projects.tsiouchas.metrics.3': 'Solid foundation for future expansion',

    'projects.sidiropoulos.challenge': 'The clinic needed a modern presence that inspires trust and facilitates patient access to information.',
    'projects.sidiropoulos.solution': 'We created a website with clear structure, medical content, and integrated contact form.',
    'projects.sidiropoulos.result': 'Modern presence that inspires trust and facilitates patient access',
    'projects.sidiropoulos.metrics.1': '90% positive feedback from users',
    'projects.sidiropoulos.metrics.2': 'Reduction in phone inquiries',
    'projects.sidiropoulos.metrics.3': 'Better position in local searches',

    'projects.underground.challenge': 'The brand wanted to strengthen its global presence in electronic music and fashion products.',
    'projects.underground.solution': 'We created a professional website, developed dynamic identity, and implemented holistic marketing strategy with content, products, and funnels.',
    'projects.underground.result': 'Strengthened global presence in electronic music and fashion products',
    'projects.underground.metrics.1': '10,000+ visits/month',
    'projects.underground.metrics.2': '5,000+ product sales',
    'projects.underground.metrics.3': 'Top 80 worldwide in category',
    'projects.underground.metrics.4': 'Audience growth through YouTube and direct traffic',

    'projects.lensory.challenge': 'The photography studio needed a modern website that showcases their work and reflects their artistic vision.',
    'projects.lensory.solution': 'We designed and developed an elegant WordPress website, improved corporate identity, and implemented SEO optimization for photography keywords.',
    'projects.lensory.result': 'Minimalist, fast, and immersive portfolio experience that lets the visuals speak for themselves',
    'projects.lensory.metrics.1': 'Enhanced credibility and increased client inquiries',
    'projects.lensory.metrics.2': 'Optimization for image-heavy pages',
    'projects.lensory.metrics.3': 'Strong online presence and recognition',

    'projects.invoicedirect.challenge': 'Freelancers and small businesses needed a professional invoicing platform that is easy to use and allows quick invoice creation.',
    'projects.invoicedirect.solution': 'We developed a SaaS application with custom branding, conversion-optimized design, JavaScript/TypeScript front-end, and back-end for dynamic invoice creation.',
    'projects.invoicedirect.result': 'Luxury and reliable invoicing platform with conversion-driven experience',
    'projects.invoicedirect.metrics.1': 'Professional invoicing experience on desktop and mobile',
    'projects.invoicedirect.metrics.2': 'Invoice creation in seconds',
    'projects.invoicedirect.metrics.3': 'Faster payment collection',

    'projects.gkoutzioulis.challenge': 'Ioannis Gkoutzioulis needed a professional portfolio to showcase his skills as a web developer and music producer.',
    'projects.gkoutzioulis.solution': 'We designed a custom portfolio with Uncode theme, defined brand positioning, and optimized for performance and accessibility.',
    'projects.gkoutzioulis.result': 'Modern, responsive website that communicates technical and artistic personality',
    'projects.gkoutzioulis.metrics.1': 'Professional identity and project presentation',
    'projects.gkoutzioulis.metrics.2': 'Improved UX for mobile users',
    'projects.gkoutzioulis.metrics.3': 'Solid foundation for freelance brand',

    'projects.tradesignalx.challenge': 'The trading community needed a one-page website designed for conversion and member growth.',
    'projects.tradesignalx.solution': 'We designed UX and architecture for single page, developed with Astro and JavaScript, and integrated newsletter and Discord.',
    'projects.tradesignalx.result': 'Clean, fast, and conversion-focused landing experience for trading community',
    'projects.tradesignalx.metrics.1': 'Increase in sign-ups and engagement',
    'projects.tradesignalx.metrics.2': 'Mobile-first optimization',
    'projects.tradesignalx.metrics.3': 'Position as modern and reliable community',

    'projects.pieriastrom.challenge': 'Pieria Strom needed a high-performance store that serves retail and wholesale buyers, supports real-time product setup, and stays fast on WordPress.',
    'projects.pieriastrom.solution': 'We designed a clarity-first interface, engineered a custom WooCommerce build with a dynamic pricing plugin, and connected advanced shipping, tax, and dimension logic in a mobile-first experience.',
    'projects.pieriastrom.result': 'High-performance eCommerce platform powering real-time configuration, automated pricing, and a conversion-focused journey that positions the brand as a modern Greek leader.',
    'projects.pieriastrom.metrics.1': 'Custom website design prioritizing clarity and mobile-first UX',
    'projects.pieriastrom.metrics.2': 'Dynamic eCommerce development on WooCommerce',
    'projects.pieriastrom.metrics.3': 'WordPress & WooCommerce engineering',
    'projects.pieriastrom.metrics.4': 'Custom plugin for dynamic price calculation and wholesale logic',
    'projects.pieriastrom.metrics.5': 'Advanced shipping, tax, and product dimension integration',
    'projects.pieriastrom.metrics.6': 'SEO-ready structure with analytics tracking',

    // Common
    'common.back': 'Back',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',

    // Tetris game translations
    'tetris.title': 'Game',
    'tetris.back': 'Back',
    'tetris.score': 'Score',
    'tetris.level': 'Level',
    'tetris.lines': 'Lines',
    'tetris.pause': 'Pause',
    'tetris.continue': 'Continue Game',
    'tetris.gameover': 'Game Over!',
    'tetris.finalscore': 'Final Score:',
    'tetris.newgame': 'New Game',
    'tetris.quickdrop': 'Quick Drop',

    // AI Assistant translations
    'ai.title': 'PixelPro AI Assistant',
    'ai.online': 'Online',
    'ai.placeholder': 'Ask me anything about our services...',
    'ai.powered': 'Powered by Gemini AI • Press Enter to send',
    'ai.welcome': 'Hello! I\'m PixelPro\'s AI assistant. I can help you with questions about our digital services, packages, and pricing. How can I assist you today?',
    'ai.error': `I'm sorry, I'm having trouble connecting right now. Please try again or contact us directly at ${CONTACT.email}`,
    // Checklist Lead Magnet
    'checklist.eyebrow': 'Free PDF Checklist',
    'checklist.title': 'Free Professional Website Checklist',
    'checklist.subtitle': 'See in 10 minutes if your website is costing you customers',
    'checklist.bulletsLead': 'With this checklist you will:',
    'checklist.bullets': [
      'See what’s missing to generate more calls & enquiries',
      'Check key areas: mobile, speed, SEO, and contact',
      'Get a clear action list for immediate improvements'
    ],
    'checklist.hero.badges': ['10 minutes', '5 critical areas', 'Action list'],
    'checklist.previewNote': 'A practical, no‑fluff checklist to quickly assess and improve your website.',
    'checklist.form.title': 'Download the free PDF',
    'checklist.form.subtitle': 'Enter your details for instant access to the free checklist.',
    'checklist.form.nameLabel': 'Name',
    'checklist.form.emailLabel': 'Email',
    'checklist.form.namePlaceholder': 'Your name',
    'checklist.form.emailPlaceholder': 'Your email',
    'checklist.form.submit': 'Continue',
    'checklist.form.loading': 'Submitting...',
    'checklist.form.privacy': 'No spam. You’ll receive 1–2 useful emails.',
    'checklist.form.nameError': 'Name is required.',
    'checklist.form.emailError': 'Email is required.',
    'checklist.form.emailInvalid': 'Please enter a valid email.',
    'checklist.form.submitError': 'Something went wrong. Please try again.',
    'checklist.included.title': 'What the checklist includes',
    'checklist.included.subtitle': '',
    'checklist.included.items.0.title': 'Speed & Performance',
    'checklist.included.items.0.desc': 'Issues that slow your site and reduce visitor trust.',
    'checklist.included.items.1.title': 'Mobile experience',
    'checklist.included.items.1.desc': 'Check if your site works smoothly on phones.',
    'checklist.included.items.2.title': 'SEO & Contact',
    'checklist.included.items.2.desc': 'Basics for Google visibility and easy contact.',
    'checklist.steps.title': 'How it works',
    'checklist.steps.subtitle': 'Simple. Fast. Useful.',
    'checklist.steps.items.0.title': 'Download',
    'checklist.steps.items.0.desc': 'Get the PDF instantly after the form.',
    'checklist.steps.items.1.title': 'Check',
    'checklist.steps.items.1.desc': 'See what’s in place and what’s missing.',
    'checklist.steps.items.2.title': 'Improve',
    'checklist.steps.items.2.desc': 'Follow the action list for immediate fixes.',
    'checklist.side.eyebrow': 'WHAT YOU GET',
    'checklist.side.title': 'What you’ll get',
    'checklist.side.description': 'A clear, actionable review with priorities.',
    'checklist.side.highlights': [
      'Ready-to-use checklist for fast review',
      'Focus on conversions and lead generation',
      'Clear improvement areas without jargon'
    ],
    'checklist.thankYou.eyebrow': 'ALL SET!',
    'checklist.thankYou.title': 'All set!',
    'checklist.thankYou.subtitle': 'Download the PDF checklist',
    'checklist.thankYou.download': 'Download PDF',
    'checklist.thankYou.secondaryCta': 'Request a Free Website Audit',
  }
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('el');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('pixelpro-language') as Language;
    if (savedLanguage === 'el' || savedLanguage === 'en') {
      setLanguage(savedLanguage);
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('pixelpro-language', lang);
    document.documentElement.lang = lang;
  };

  const translateValue = (key: string): TranslationValue | undefined => {
    return translations[language]?.[key];
  };

  const t = (key: string): string => {
    const value = translateValue(key);
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value[0] ?? key;
    }
    return key;
  };

  const tList = (key: string): string[] => {
    const value = translateValue(key);
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return [value];
    }
    return [key];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t, tList }}>
      {children}
    </LanguageContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
